import React, { useState, useEffect } from "react";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";

function ReviewDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState("week");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const loadStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (period === "today") {
                const today = new Date().toISOString().slice(0, 10);
                params.from = today;
                params.to = today + "T23:59:59";
            } else if (period === "week") {
                const d = new Date();
                d.setDate(d.getDate() - 7);
                params.from = d.toISOString().slice(0, 10);
            } else if (period === "month") {
                const d = new Date();
                d.setMonth(d.getMonth() - 1);
                params.from = d.toISOString().slice(0, 10);
            } else if (period === "custom") {
                if (fromDate) params.from = fromDate;
                if (toDate) params.to = toDate;
            }
            const data = await api.reviewStats(params);
            setStats(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStats();
    }, [period, fromDate, toDate]);

    const t = stats?.totals || {};
    const reviewers = stats?.reviewers || [];

    const formatMinutes = (mins) => {
        if (!mins) return "0m";
        if (mins < 60) return `${mins}m`;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${h}h ${m}m`;
    };

    return (
        <div>
            <div className="page-header">
                <h2>Review Dashboard</h2>
                <p>Performance metrics for scan reviews</p>
            </div>

            {/* Period selector */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                {["today", "week", "month", "custom"].map(p => (
                    <button
                        key={p}
                        onClick={() => setPeriod(p)}
                        style={{
                            padding: "6px 14px", fontSize: 13, fontWeight: 500, cursor: "pointer",
                            border: "1px solid " + (period === p ? "var(--primary)" : "var(--border)"),
                            borderRadius: 6,
                            background: period === p ? "var(--primary)" : "var(--bg-secondary)",
                            color: period === p ? "#fff" : "var(--text-primary)",
                        }}
                    >
                        {p === "today" ? "Today" : p === "week" ? "This Week" : p === "month" ? "This Month" : "Custom"}
                    </button>
                ))}
                {period === "custom" && (
                    <>
                        <input
                            type="date"
                            className="filter-select"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            style={{ minWidth: 140, marginLeft: 8 }}
                        />
                        <span style={{ color: "var(--text-muted)" }}>to</span>
                        <input
                            type="date"
                            className="filter-select"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            style={{ minWidth: 140 }}
                        />
                    </>
                )}
            </div>

            {error ? (
                <div className="empty-state">
                    <h3>Failed to load dashboard</h3>
                    <p>{error}</p>
                    <button className="retry-btn" onClick={loadStats}>Retry</button>
                </div>
            ) : loading ? (
                <LoadingSpinner message="Loading review dashboard..." />
            ) : (
            <>
            {/* KPI cards */}
            <div className="stats-grid" style={{ marginBottom: 24 }}>
                <div className="stat-card">
                    <div className="stat-label">Reviews Completed</div>
                    <div className="stat-value">{t.completedReviews || 0}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Avg Issues / Review</div>
                    <div className="stat-value">{t.avgIssuesPerReview || 0}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Avg Time / Review</div>
                    <div className="stat-value">{formatMinutes(t.avgReviewMinutes)}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Open Issues</div>
                    <div className="stat-value" style={{ color: (t.openIssues || 0) > 0 ? "#ef4444" : undefined }}>{t.openIssues || 0}</div>
                    <div className="stat-change neutral">total backlog (all time)</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Outstanding</div>
                    <div className="stat-value" style={{ color: (t.outstanding || 0) > 0 ? "#ef4444" : undefined }}>{t.outstanding || 0}</div>
                    <div className="stat-change neutral">raised in period, still open</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Awaiting Verification</div>
                    <div className="stat-value" style={{ color: (t.awaitingVerification || 0) > 0 ? "#f97316" : undefined }}>{t.awaitingVerification || 0}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Closed Issues</div>
                    <div className="stat-value" style={{ color: "#22c55e" }}>{t.closedIssues || 0}</div>
                </div>
            </div>

            {/* Status breakdown */}
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Status Breakdown</h3>
                <div style={{ display: "flex", gap: 0, borderRadius: 8, overflow: "hidden", height: 32 }}>
                    {t.unreviewedScans > 0 && (
                        <div style={{
                            flex: t.unreviewedScans,
                            background: "var(--bg-secondary)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 12, fontWeight: 600, color: "var(--text-muted)",
                            minWidth: 40
                        }}>
                            {t.unreviewedScans} open
                        </div>
                    )}
                    {t.inProgressReviews > 0 && (
                        <div style={{
                            flex: t.inProgressReviews,
                            background: "rgba(59,130,246,0.2)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 12, fontWeight: 600, color: "#3b82f6",
                            minWidth: 40
                        }}>
                            {t.inProgressReviews} reviewing
                        </div>
                    )}
                    {t.completedReviews > 0 && (
                        <div style={{
                            flex: t.completedReviews,
                            background: "rgba(34,197,94,0.2)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 12, fontWeight: 600, color: "#22c55e",
                            minWidth: 40
                        }}>
                            {t.completedReviews} reviewed
                        </div>
                    )}
                </div>
            </div>

            {/* Issue pipeline per period */}
            <div className="stats-grid" style={{ marginBottom: 24 }}>
                <div className="stat-card">
                    <div className="stat-label">Issues Opened</div>
                    <div className="stat-value" style={{ color: "#ef4444" }}>{t.issuesOpened || 0}</div>
                    <div className="stat-change neutral">in period</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Issues Verified</div>
                    <div className="stat-value" style={{ color: "#f97316" }}>{t.awaitingVerification || 0}</div>
                    <div className="stat-change neutral">awaiting</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Issues Closed</div>
                    <div className="stat-value" style={{ color: "#22c55e" }}>{t.closedIssues || 0}</div>
                    <div className="stat-change neutral">resolved</div>
                </div>
            </div>

            {/* Open issues with aging */}
            {(t.openIssues > 0 || t.awaitingVerification > 0) && (
                <div style={{ marginBottom: 24 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Open Issue Aging</h3>
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-label">Under 1 Day</div>
                            <div className="stat-value" style={{ color: "#22c55e" }}>{t.agingUnder1Day || 0}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">1-3 Days</div>
                            <div className="stat-value" style={{ color: "#3b82f6" }}>{t.agingDays1to3 || 0}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">4-7 Days</div>
                            <div className="stat-value" style={{ color: "#f97316" }}>{t.agingDays4to7 || 0}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Over 7 Days</div>
                            <div className="stat-value" style={{ color: "#ef4444" }}>{t.agingOver7Days || 0}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reviewer breakdown table */}
            <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Reviewer Breakdown</h3>
                {reviewers.length === 0 ? (
                    <div className="empty-state">
                        <p>No review data for this period.</p>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                            <thead>
                                <tr style={{ borderBottom: "2px solid var(--border)", textAlign: "left" }}>
                                    <th style={{ padding: "8px 12px", fontWeight: 600 }}>Reviewer</th>
                                    <th style={{ padding: "8px 12px", fontWeight: 600, textAlign: "center" }}>Completed</th>
                                    <th style={{ padding: "8px 12px", fontWeight: 600, textAlign: "center" }}>Avg Issues</th>
                                    <th style={{ padding: "8px 12px", fontWeight: 600, textAlign: "center" }}>Avg Time</th>
                                    <th style={{ padding: "8px 12px", fontWeight: 600, textAlign: "center" }}>In Progress</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviewers.map((r, i) => (
                                    <tr key={r.reviewerEmail || i} style={{ borderBottom: "1px solid var(--border)" }}>
                                        <td style={{ padding: "8px 12px" }}>
                                            <div style={{ fontWeight: 500 }}>{r.reviewerName || r.reviewerEmail}</div>
                                            {r.reviewerName && (
                                                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{r.reviewerEmail}</div>
                                            )}
                                        </td>
                                        <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600 }}>
                                            {r.completedCount}
                                        </td>
                                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                            {r.avgIssues}
                                        </td>
                                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                            {formatMinutes(r.avgMinutes)}
                                        </td>
                                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                            {r.inProgressCount > 0 ? (
                                                <span style={{ color: "#3b82f6", fontWeight: 600 }}>{r.inProgressCount}</span>
                                            ) : "0"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            </>
            )}
        </div>
    );
}

export default ReviewDashboard;
